import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ImagePlus, Loader2, PencilLine, Send, X } from "lucide-react";
import { instagramApi } from "../../api/instagram";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";
import { InstagramV2AiAssistant } from "./InstagramV2AiAssistant";
import {
  InstagramV2MediaPicker,
  uploadInstagramV2PendingMediaFiles,
  type InstagramV2PendingMedia,
  type InstagramV2SelectedMedia,
} from "./InstagramV2MediaPicker";

type PublishMode = "draft" | "now";

type InstagramV2ComposerState = {
  managementTitle: string;
  contentType: "IMAGE";
  selectedMedia: InstagramV2SelectedMedia[];
  pendingMedia: InstagramV2PendingMedia[];
  caption: string;
  hashtags: string[];
  publishMode: PublishMode;
  submitting: boolean;
  uploading: boolean;
  error: string | null;
  titleError: string | null;
  mediaError: string | null;
};

const INITIAL_STATE: InstagramV2ComposerState = {
  managementTitle: "",
  contentType: "IMAGE",
  selectedMedia: [],
  pendingMedia: [],
  caption: "",
  hashtags: [],
  publishMode: "draft",
  submitting: false,
  uploading: false,
  error: null,
  titleError: null,
  mediaError: null,
};

function buildFinalCaption(caption: string, hashtags: string[]): string {
  const normalized = hashtags
    .map((tag) => tag.trim().replace(/^#+/, ""))
    .filter(Boolean)
    .map((tag) => `#${tag}`);
  if (normalized.length === 0) return caption.trim();
  return `${caption.trim()}\n\n${normalized.join(" ")}`.trim();
}

export function InstagramV2ComposerModal({
  open,
  onClose,
  onSaved,
  initialTitle,
  initialCaption,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialTitle?: string;
  initialCaption?: string;
}) {
  const { pick } = useUiLanguage();
  const [state, setState] = useState<InstagramV2ComposerState>(INITIAL_STATE);
  const submitRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setState((prev) => ({
      ...INITIAL_STATE,
      managementTitle: initialTitle ?? "",
      caption: initialCaption ?? "",
      selectedMedia: prev.selectedMedia,
      pendingMedia: prev.pendingMedia,
    }));
  }, [initialCaption, initialTitle, open]);

  const handleClose = useCallback(() => {
    state.pendingMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    onClose();
    setState(INITIAL_STATE);
  }, [onClose, state.pendingMedia]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handleClose, open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const setField = useCallback(
    <K extends keyof InstagramV2ComposerState>(key: K, value: InstagramV2ComposerState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handlePendingMediaChange = useCallback((next: InstagramV2PendingMedia[]) => {
    setField("pendingMedia", next);
  }, [setField]);

  const handleSelectedMediaChange = useCallback((next: InstagramV2SelectedMedia[]) => {
    setState((prev) => ({
      ...prev,
      selectedMedia: next,
      mediaError: null,
    }));
  }, []);

  const handleUploadingChange = useCallback((uploading: boolean) => {
    setField("uploading", uploading);
  }, [setField]);

  const validation = useMemo(() => {
    if (state.submitting) return { ok: false, message: pick("送信中です...", "Submitting...") };
    if (state.uploading) return { ok: false, message: pick("アップロード中です...", "Uploading...") };
    if (state.selectedMedia.length === 0 && state.pendingMedia.length === 0) {
      return { ok: false, message: pick("投稿するメディアを選択してください。", "Select media for the post.") };
    }
    return { ok: true, message: null };
  }, [pick, state.pendingMedia.length, state.selectedMedia.length, state.submitting, state.uploading]);

  const handleSubmit = useCallback(async (mode: PublishMode) => {
    if (submitRef.current || state.submitting || state.uploading) return;
    if (!state.managementTitle.trim()) {
      setState((prev) => ({ ...prev, titleError: pick("管理用タイトルを入力してください。", "Enter an internal title.") }));
      return;
    }
    if (state.selectedMedia.length === 0 && state.pendingMedia.length === 0) {
      setState((prev) => ({ ...prev, mediaError: pick("投稿するメディアを選択してください。", "Select media for the post.") }));
      return;
    }

    submitRef.current = true;
    setState((prev) => ({
      ...prev,
      submitting: true,
      error: null,
      titleError: null,
      mediaError: null,
      publishMode: mode,
    }));

    try {
      const media = await uploadInstagramV2PendingMediaFiles({
        pendingMedia: state.pendingMedia,
        managementTitle: state.managementTitle,
        existing: state.selectedMedia,
        setPendingMedia: (next) => setState((prev) => ({ ...prev, pendingMedia: next })),
        setSelected: (next) => setState((prev) => ({ ...prev, selectedMedia: next })),
        setUploading: (uploading) => setState((prev) => ({ ...prev, uploading })),
      });

      const first = media[0];
      if (!first?.publicUrl) throw new Error(pick("公開 URL が取得できませんでした。", "Public URL could not be resolved."));

      const draft = await instagramApi.createDraft({
        image_url: first.publicUrl,
        caption: buildFinalCaption(state.caption, state.hashtags),
        content_type: state.contentType,
      });

      if (mode === "now") {
        await instagramApi.publishDraft(draft.id);
      }

      onSaved();
      handleClose();
    } catch (e) {
      setState((prev) => ({
        ...prev,
        submitting: false,
        error: `${pick("保存に失敗しました", "Save failed")}: ${e instanceof Error ? e.message : String(e)}`,
      }));
    } finally {
      submitRef.current = false;
    }
  }, [handleClose, onSaved, state]);

  if (!open) return null;

  return (
    <div className="igv2-compose-backdrop" role="presentation" onClick={handleClose}>
      <div className="igv2-compose-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="igv2-compose-header">
          <div>
            <h2>{pick("新規投稿を作成", "Create a post")}</h2>
          </div>
          <button type="button" className="igv2-compose-icon-button" onClick={handleClose} aria-label={pick("閉じる", "Close")}>
            <X size={16} />
          </button>
        </header>

        <div className="igv2-compose-body igv2-compose-body--e">
          <div className="igv2-compose-main">
            <section className="igv2-compose-panel igv2-compose-panel--media">
              <div className="igv2-compose-section-head">
                <span>1.</span>
                <h3>{pick("メディアを選択", "Choose media")}</h3>
              </div>
              <InstagramV2MediaPicker
                managementTitle={state.managementTitle}
                selected={state.selectedMedia}
                pendingMedia={state.pendingMedia}
                onPendingChange={handlePendingMediaChange}
                onChange={handleSelectedMediaChange}
                onUploadingChange={handleUploadingChange}
              />
              {state.mediaError ? <div className="igv2-compose-error">{state.mediaError}</div> : null}
            </section>

            <section className="igv2-compose-panel igv2-compose-panel--editor">
              <div className="igv2-compose-section-head">
                <span>2.</span>
                <h3>{pick("投稿内容を編集", "Edit content")}</h3>
              </div>

              <div className="igv2-compose-form-grid">
                <div className="igv2-compose-field-block">
                  <label className="igv2-compose-label">
                    {pick("管理用タイトル", "Internal title")}
                    <span className="igv2-compose-required">*</span>
                  </label>
                  <input
                    className="igv2-compose-input"
                    placeholder={pick("例: 夏キャンペーン商品紹介", "e.g. Summer campaign feature")}
                    value={state.managementTitle}
                    onChange={(event) => {
                      setField("managementTitle", event.target.value);
                      if (state.titleError) setField("titleError", null);
                    }}
                  />
                  <div className="igv2-compose-hint">{pick("保存時のファイル名に使います。Instagram には保存されません。", "Used for internal file naming only. It will not be sent to Instagram.")}</div>
                  {state.titleError ? <div className="igv2-compose-error">{state.titleError}</div> : null}
                </div>

                <div className="igv2-compose-meta-row">
                  <div className="igv2-compose-field-block">
                    <label className="igv2-compose-label">{pick("コンテンツタイプ", "Content type")}</label>
                    <div className="igv2-compose-static-pill">{pick("画像", "Image")}</div>
                  </div>
                </div>
              </div>

              <label className="igv2-compose-label">{pick("キャプション", "Caption")}</label>
              <textarea
                className="igv2-compose-textarea"
                placeholder={pick("投稿の本文を入力...\n\nハッシュタグは下の入力欄に追加してください。", "Write your post copy...\n\nAdd hashtags in the field below.")}
                value={state.caption}
                onChange={(event) => setField("caption", event.target.value)}
              />

              <HashtagEditor hashtags={state.hashtags} onChange={(next) => setField("hashtags", next)} />
            </section>
          </div>

          <aside className="igv2-compose-side">
            <section className="igv2-compose-panel igv2-compose-panel--publish">
              <div className="igv2-compose-section-head">
                <span>3.</span>
                <h3>{pick("公開設定", "Publishing")}</h3>
              </div>

              <div className="igv2-compose-publish">
                <label className={`igv2-compose-publish-row ${state.publishMode === "draft" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="igv2-publish-mode"
                    checked={state.publishMode === "draft"}
                    onChange={() => setField("publishMode", "draft")}
                  />
                  <span>{pick("下書きとして保存", "Save as draft")}</span>
                </label>
                <label className={`igv2-compose-publish-row ${state.publishMode === "now" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="igv2-publish-mode"
                    checked={state.publishMode === "now"}
                    onChange={() => setField("publishMode", "now")}
                  />
                  <span>{pick("今すぐ公開", "Publish now")}</span>
                </label>
              </div>

              {state.error ? (
                <div className="igv2-compose-error">
                  <AlertTriangle size={12} />
                  {state.error}
                </div>
              ) : null}
            </section>

            <section className="igv2-compose-panel igv2-compose-panel--assistant">
              <InstagramV2AiAssistant
                managementTitle={state.managementTitle}
                caption={state.caption}
                selectedMediaName={state.selectedMedia[0]?.fileName ?? state.pendingMedia[0]?.originalFile.name ?? null}
                onApplyCaption={(text) => setField("caption", text)}
                onApplyHashtags={(tags) => setField("hashtags", tags)}
                collapsible
                defaultCollapsed
              />
            </section>
          </aside>
        </div>

        <footer className="igv2-compose-footer">
          <button type="button" className="igv2-compose-ghost-button" onClick={handleClose} disabled={state.submitting}>
            {pick("キャンセル", "Cancel")}
          </button>
          <div className="igv2-compose-footer-actions">
            <button type="button" className="igv2-compose-ghost-button" onClick={() => handleSubmit("draft")} disabled={!validation.ok}>
              {state.submitting && state.publishMode === "draft" ? <Loader2 size={14} className="igv2-spin" /> : <ImagePlus size={14} />}
              {pick("下書きを保存", "Save draft")}
            </button>
            <button type="button" className="igv2-compose-gradient-button" onClick={() => handleSubmit("now")} disabled={!validation.ok}>
              {state.submitting && state.publishMode === "now" ? <Loader2 size={14} className="igv2-spin" /> : <Send size={14} />}
              {pick("今すぐ公開", "Publish now")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function HashtagEditor({
  hashtags,
  onChange,
}: {
  hashtags: string[];
  onChange: (next: string[]) => void;
}) {
  const { pick } = useUiLanguage();
  const [draft, setDraft] = useState("");

  const add = () => {
    const value = draft.trim().replace(/^#+/, "").replace(/\s+/g, "");
    if (!value) return;
    if (hashtags.includes(value)) {
      setDraft("");
      return;
    }
    onChange([...hashtags, value]);
    setDraft("");
  };

  return (
    <div>
      <label className="igv2-compose-label">{pick("ハッシュタグ", "Hashtags")}</label>
      <div className="igv2-compose-hashtag-input-row">
        <input
          className="igv2-compose-input"
          placeholder={pick("例: 新商品", "e.g. new-product")}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="igv2-compose-ghost-button" onClick={add} disabled={!draft.trim()}>
          <PencilLine size={14} />
          {pick("追加", "Add")}
        </button>
      </div>

      {hashtags.length > 0 ? (
        <div className="igv2-compose-hashtag-list">
          {hashtags.map((tag) => (
            <span key={tag} className="igv2-compose-hashtag-chip">
              #{tag}
              <button type="button" onClick={() => onChange(hashtags.filter((item) => item !== tag))} aria-label={pick(`${tag} を削除`, `Remove ${tag}`)}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="igv2-compose-hint">{pick("ハッシュタグは保存時にキャプション末尾へ自動で結合されます。", "Hashtags are automatically appended to the end of the caption when saved.")}</div>
      )}
    </div>
  );
}
